import type { RegisteredComponent } from "@builder.io/sdk-react";

import { MainNavDefinition } from "~/components/hec-header/mobile-nav/main-nav-definition";

import { TwoUpImageTextDefinition } from "./blocks/components/2-up-image-text";
import {
  AccordionDefinition,
  AccordionDetailDefinition,
  AccordionItemDefinition,
  AccordionTitleDefinition,
} from "./blocks/components/accordion";
import { AccountDrawerDefinition } from "./blocks/components/account-drawer";
import { AlertBarDefinition } from "./blocks/components/alert-bar";
import { ArticleCardDefinition } from "./blocks/components/article-card";
import { ArticleGridDefinition } from "./blocks/components/article-grid";
import { ArticleTopicCardDefinition } from "./blocks/components/article-topic-card";
import { AuthContentDefinition } from "./blocks/components/auth-content";
import {
  CarouselButtonDefinition,
  CarouselContentDefinition,
  CarouselContentNavDefinition,
  CarouselDefinition,
  DeprecatedCarouselContentDefinition,
  DeprecatedCarouselDefinition,
} from "./blocks/components/carousel";
import { CategoryFiltersDefinition } from "./blocks/components/category-filters";
import { ContactUsFormDefinition } from "./blocks/components/contact-us-form";
import { ContentCardsDefinition } from "./blocks/components/content-cards";
import {
  ContentTabsDefinition,
  TabButtonDefinition,
  TabPanelDefinition,
} from "./blocks/components/content-tabs";
import { LinkWrapperDefinition } from "./blocks/components/cta";
import { ButtonDefinition } from "./blocks/components/cta/button";
import { DownloadAssetButtonDefinition } from "./blocks/components/cta/downloadAssetButton";
import { LinkDefinition } from "./blocks/components/cta/link";
import { ToggleTextButtonDefinition } from "./blocks/components/cta/toggleTextButton";
import { DynamicProductTileDefinition } from "./blocks/components/dynamic-product-tile";
import { EmailSignupDefinition } from "./blocks/components/email-signup";
import { FeatureCardsDefinition } from "./blocks/components/feature-cards";
import { FeatureHeroDefinition } from "./blocks/components/feature-hero";
import { FeaturedProductSliderDefinition } from "./blocks/components/featured-product-slider";
import { FeaturedThreeUpDefinition } from "./blocks/components/featured-three-up";
import { FsaDeadlineTickerDefinition } from "./blocks/components/fsa-deadline-ticker";
import { FsaSpenderPageDefinition } from "./blocks/components/fsa-spender-page";
import { GoalTileDefinition } from "./blocks/components/goal";
import { GoalBannerDefinition } from "./blocks/components/goal-banner";
import { GuestContentDefinition } from "./blocks/components/guest-content";
import {
  SingleColumnHeroDefinition,
  ThreeColumnHeroDefinition,
  TwoColumnHeroDefinition,
} from "./blocks/components/hero";
import { HomepageCarouselGridDefinition } from "./blocks/components/homepage-carousel-grid";
import { IconLinksGridDefinition } from "./blocks/components/icon-links-grid";
import { IconDefinition } from "./blocks/components/icon-picker";
import { ImageTextCarouselGridDefinition } from "./blocks/components/image-text-carousel-grid";
import { InsidersClaimFormDefinition } from "./blocks/components/insiders-claim-form";
import {
  LearningCenterArticleBreadcrumbDefinition,
  LegacyBreadcrumbDefinition,
} from "./blocks/components/learning-center-article-breadcrumb";
import { LearningCenterBreadcrumbDefinition } from "./blocks/components/learning-center-breadcrumb";
import { LearningCenterLevelsDefinition } from "./blocks/components/learning-center-levels";
import { LearningCenterModuleDefinition } from "./blocks/components/learning-center-module";
import { GoalQuestionnaireDefinition } from "./blocks/components/learning-center-questionnaire";
import { LmnAllowedContentDefinition } from "./blocks/components/lmn-allowed-content";
import { LmnSuppressedContentDefinition } from "./blocks/components/lmn-suppressed-content";
// NOTE: Builder's built-in Image component is used intentionally.
// A custom ImageDefinition existed but its schema (string enum aspectRatio,
// no backgroundSize/width/height/sizes) is incompatible with the data
// Builder's visual editor produces. The original hec-composable also used
// the built-in. Do NOT re-register a custom "Image" override here.
// Catch-all pages rewrite CDN urls via `rewriteBuilderMediaInPlace` and
// flag above-the-fold `highPriority` via COM-181
// (`prioritizeAboveFoldImages`) in `transformBuilderPageEntry`.
import { PartnerFormDefinition } from "./blocks/components/partner-form";
import { PerksAndRewardsSectionDefinition } from "./blocks/components/perks-and-rewards";
import { PlpBottomBannerDefinition } from "./blocks/components/plp-bottom-banner";
import { PlpTopBannerDefinition } from "./blocks/components/plp-top-banner";
import { ProductCarouselDefinition } from "./blocks/components/product-carousel";
import {
  ProductRecommendationsDefinition,
  ProductRecommendationsLegacyDefinition,
} from "./blocks/components/product-recommendations";
import { SignOutButtonDefinition } from "./blocks/components/sign-out-button";
import { SpenderCartDefinition } from "./blocks/components/spender-cart";
import { SpenderChipRowDefinition } from "./blocks/components/spender-chip-row";
import { SpenderGridDefinition } from "./blocks/components/spender-grid";
import { EngagementStallModalDefinition } from "./blocks/components/telehealth/engagement-stall-modal";
import { FirstQuestionDefinition } from "./blocks/components/telehealth/first-question";
import { Glp1PricingDefinition } from "./blocks/components/telehealth/glp1-pricing";
import { TelehealthHowItWorksDefinition } from "./blocks/components/telehealth/how-it-works";
import { TextDefinition } from "./blocks/components/text";
import { ThemeBoxDefinition } from "./blocks/components/theme-box";
import { TpaBalanceDefinition } from "./blocks/components/tpa-balance";
import { TpaLearningCenterConsentDefinition } from "./blocks/components/tpa-learning-center-consent";
import { TrustArcBannerDefinition } from "./blocks/components/trustarc-banner";
import { BodyCopyDefinition } from "./blocks/components/typography/body-copy";
import { EyebrowDefinition } from "./blocks/components/typography/eyebrow";
import { HeadingDefinition } from "./blocks/components/typography/heading";
import { UserNameDefinition } from "./blocks/components/user-name";
import { VideoImageTextDefinition } from "./blocks/components/video-image-text";

export const customComponents: RegisteredComponent[] = [
  FirstQuestionDefinition,
  EngagementStallModalDefinition,
  Glp1PricingDefinition,
  TelehealthHowItWorksDefinition,
  AccordionDefinition,
  AccordionItemDefinition,
  AccordionTitleDefinition,
  AccordionDetailDefinition,
  CategoryFiltersDefinition,
  TextDefinition,
  // Themed wrapper <div> (light/medium/dark). Ported from composable so entries
  // authored with a `ThemeBox` block (LC landing + module pages) render instead
  // of logging the SDK "unregistered component" warning. Registry-only — NOT
  // mounted in the layout/root; resolves only where an entry uses the block.
  ThemeBoxDefinition,
  LinkDefinition,
  LinkWrapperDefinition,
  ButtonDefinition,
  HeadingDefinition,
  BodyCopyDefinition,
  EyebrowDefinition,
  CarouselDefinition,
  CarouselContentDefinition,
  CarouselButtonDefinition,
  CarouselContentNavDefinition,
  // Deprecated bare-named Carousel/CarouselContent (composable pre-`v1:` shape)
  // so legacy entries (e.g. the LC-landing "Featured Articles") render. Additive
  // — `v1:` versions above are untouched. (SFCC-11736.)
  DeprecatedCarouselDefinition,
  DeprecatedCarouselContentDefinition,
  ContentCardsDefinition,
  SingleColumnHeroDefinition,
  TwoColumnHeroDefinition,
  ThreeColumnHeroDefinition,
  HomepageCarouselGridDefinition,
  ImageTextCarouselGridDefinition,
  EmailSignupDefinition,
  IconLinksGridDefinition,
  IconDefinition,
  TrustArcBannerDefinition,
  MainNavDefinition,
  AlertBarDefinition,
  AuthContentDefinition,
  ContentTabsDefinition,
  TabButtonDefinition,
  TabPanelDefinition,
  GuestContentDefinition,
  LmnAllowedContentDefinition,
  LmnSuppressedContentDefinition,
  SignOutButtonDefinition,
  UserNameDefinition,
  AccountDrawerDefinition,
  PerksAndRewardsSectionDefinition,
  ProductRecommendationsDefinition,
  // Additive alias for MRT-authored entries stored under the Title-Case name
  // "Product Recommendations" (e.g. HSA Learning Center article recs). Registry-
  // only; routes category/pod options to the existing browse/recs paths so
  // legacy entries render instead of logging the SDK "unregistered component"
  // warning. (SFCC-11794.)
  ProductRecommendationsLegacyDefinition,
  ProductCarouselDefinition,
  // Single Builder-configurable product tile + the 3-up Featured Product Slider.
  DynamicProductTileDefinition,
  FeaturedProductSliderDefinition,
  // COM-945: the Featured 3-Up, Telehealth Feature and Hero symbols as
  // components that can go on any page.
  FeaturedThreeUpDefinition,
  FeatureCardsDefinition,
  FeatureHeroDefinition,
  ToggleTextButtonDefinition,
  DownloadAssetButtonDefinition,
  PlpTopBannerDefinition,
  PlpBottomBannerDefinition,
  VideoImageTextDefinition,
  TwoUpImageTextDefinition,
  TpaBalanceDefinition,
  TpaLearningCenterConsentDefinition,
  LearningCenterArticleBreadcrumbDefinition,
  // Additive alias for MRT-authored HSA article entries stored under the bare
  // name "Breadcrumb". Registry-only; renders the article breadcrumb above so
  // ~14 legacy entries stop logging the SDK "unregistered component" warning
  // and get a breadcrumb back. (COM-445.)
  LegacyBreadcrumbDefinition,
  LearningCenterBreadcrumbDefinition,
  GoalTileDefinition,
  GoalBannerDefinition,
  LearningCenterLevelsDefinition,
  LearningCenterModuleDefinition,
  GoalQuestionnaireDefinition,
  ArticleCardDefinition,
  ArticleTopicCardDefinition,
  ArticleGridDefinition,
  FsaSpenderPageDefinition,
  FsaDeadlineTickerDefinition,
  SpenderChipRowDefinition,
  SpenderGridDefinition,
  SpenderCartDefinition,
  // COM-506: Insiders Perks points claim card (monthly / annual contribution /
  // future plans). Page-body only — the Insiders page is authored in Builder,
  // this block is the only engineered piece of it.
  InsidersClaimFormDefinition,
  // COM-735: Contact Us form (FSA + HSA). Page-body only — drop it into any
  // Builder page; the surrounding callout and contacts are authored content.
  ContactUsFormDefinition,
  // COM-737: Become a Partner form (FSA + HSA). Page-body only; the navy card
  // and copy around it are authored content.
  PartnerFormDefinition,
];
